/**
 * Job-scoped audit timeline.
 *
 * The audit pipeline has existed since the identity work — append-only events
 * with actor, correlation id and metadata — but the dashboard only ever
 * rendered a COUNT of them ("N recent audit events available"). A trail nobody
 * can read is not an audit trail, and re-filing a photo made that gap matter:
 * it is the one operation that could make a failing submission look complete.
 *
 * Two things this has to get right to be worth having:
 *
 *   - **Grouping by correlation id.** One bulk import writes an event per
 *     photo. Sixty unrelated-looking rows is the same as no timeline; the same
 *     sixty under one collapsible header is a fact you can take in at a glance.
 *   - **Readable summaries.** The emitters write `summary` to stand alone, so
 *     scanning never requires expanding metadata. The raw payload stays one
 *     click away for when it does.
 */

import { useEffect, useState } from 'react';
import type { AuditEvent } from '@cer-autofill/shared';
import { fetchAuditForJob } from '../api.js';
import { formatDateTime } from '../format.js';

interface Group {
  correlationId: string;
  /** Newest event in the group — what the collapsed header shows. */
  head: AuditEvent;
  events: AuditEvent[];
}

export function AuditTimeline({ jobId }: { jobId: string }) {
  const [events, setEvents] = useState<AuditEvent[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState('');

  useEffect(() => {
    let cancelled = false;
    setEvents(null);
    setError(null);
    void fetchAuditForJob(jobId)
      .then((rows) => {
        if (!cancelled) setEvents(rows);
      })
      .catch((e: unknown) => {
        if (!cancelled)
          setError(e instanceof Error ? e.message : 'Could not load the audit trail.');
      });
    return () => {
      cancelled = true;
    };
  }, [jobId]);

  if (error) return <p className="error">{error}</p>;
  if (!events) return <p className="muted">Loading audit trail…</p>;
  if (events.length === 0) {
    return <p className="muted">No audit events recorded for this job yet.</p>;
  }

  const needle = filter.trim().toLowerCase();
  const shown = needle
    ? events.filter((e) => `${e.action} ${e.summary} ${e.actorId}`.toLowerCase().includes(needle))
    : events;
  const groups = groupByCorrelation(shown);

  return (
    <div className="audit-timeline">
      <label className="audit-timeline__filter">
        <span>Filter</span>
        <input
          type="search"
          value={filter}
          placeholder="Action, actor, or summary"
          onChange={(e) => setFilter(e.target.value)}
        />
      </label>

      {groups.length === 0 && <p className="muted">Nothing matches that.</p>}

      <ol className="audit-timeline__list">
        {groups.map((group) => (
          <li key={group.correlationId}>
            {group.events.length === 1 ? (
              <AuditRow event={group.head} />
            ) : (
              <details className="audit-timeline__group">
                <summary>
                  <span className="audit-timeline__when">
                    {formatDateTime(group.head.occurredAt)}
                  </span>
                  <span className="audit-timeline__summary">
                    {summariseGroup(group)} — {group.head.actorId}
                  </span>
                </summary>
                <ol className="audit-timeline__list">
                  {group.events.map((event) => (
                    <li key={event.eventId}>
                      <AuditRow event={event} />
                    </li>
                  ))}
                </ol>
              </details>
            )}
          </li>
        ))}
      </ol>
    </div>
  );
}

function AuditRow({ event }: { event: AuditEvent }) {
  const hasMetadata = Object.keys(event.metadata).length > 0;
  return (
    <div className={`audit-row audit-row--${severityOf(event.action)}`}>
      <div className="audit-row__head">
        <span className="audit-timeline__when">{formatDateTime(event.occurredAt)}</span>
        <span className="audit-row__action">{event.action}</span>
        <span className="audit-row__actor">{event.actorId}</span>
      </div>
      <p className="audit-row__summary">{event.summary}</p>
      {hasMetadata && (
        <details className="audit-row__meta">
          <summary>Details</summary>
          <pre>{JSON.stringify(event.metadata, null, 2)}</pre>
        </details>
      )}
    </div>
  );
}

/**
 * Group related events under one row.
 *
 * `correlationId` alone is not enough: it is the server's per-REQUEST id, and a
 * bulk import uploads each photo in its own request — so sixty photos arrive
 * with sixty different correlation ids and would render as sixty rows. The
 * field client stamps a shared `batchId` in the metadata of everything from one
 * import, and that takes precedence when present.
 *
 * Order is preserved from the caller (newest first), so a group's first event
 * is its head.
 */
function groupByCorrelation(events: readonly AuditEvent[]): Group[] {
  const groups = new Map<string, Group>();
  for (const event of events) {
    const batchId = event.metadata['batchId'];
    const key = typeof batchId === 'string' && batchId ? `batch:${batchId}` : event.correlationId;
    const existing = groups.get(key);
    if (existing) existing.events.push(event);
    else groups.set(key, { correlationId: key, head: event, events: [event] });
  }
  return [...groups.values()];
}

function summariseGroup(group: Group): string {
  const actions = new Set(group.events.map((e) => e.action));
  if (actions.size === 1) return `${group.events.length} × ${group.head.action}`;
  return `${group.events.length} events`;
}

/**
 * A refusal is the row an admin is most likely to be looking for — someone
 * tried to change something and was stopped — so it gets visual weight that
 * routine activity does not.
 */
function severityOf(action: string): 'rejected' | 'normal' {
  return action.endsWith('.rejected') ? 'rejected' : 'normal';
}
